import React, { useState } from 'react';
import { TruckIcon, UserIcon, PhoneIcon, MapPinIcon, CalendarIcon, CheckCircleIcon, XCircleIcon, ClockIcon, FilterIcon, RefreshCwIcon, MessageSquareIcon, AlertTriangleIcon, ChevronRightIcon, ChevronLeftIcon, InfoIcon } from 'lucide-react';
interface AvailableTruck {
  id: string;
  truckNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  status: 'available' | 'assigned' | 'maintenance';
  truckingCompanyId: string;
  truckingCompanyName: string;
  assignedDriver?: string;
  driverName?: string;
  driverPhone?: string;
  hourlyRate: number;
  availability: {
    [date: string]: boolean;
  };
  currentLocation?: string;
  lastUpdated: string;
}
interface TruckingCompany {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  rating: number;
  truckCount: number;
}
interface AvailableTrucksProps {
  availableTrucks: AvailableTruck[];
  truckingCompanies: TruckingCompany[];
  onAssignTruckToJob: (truckId: string, jobId: string) => void;
  onContactDriver: (truckId: string) => void;
  onRequestTruck: (truckId: string, date: string) => void;
}
export const AvailableTrucks = ({
  availableTrucks,
  truckingCompanies,
  onAssignTruckToJob,
  onContactDriver,
  onRequestTruck
}: AvailableTrucksProps) => {
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [selectedTruckType, setSelectedTruckType] = useState<string>('all');
  const [viewStartDate, setViewStartDate] = useState(new Date());
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  // Helper function to get date strings for the next N days
  function getDateString(daysFromNow: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  }
  // Generate the next 7 days for the availability view
  const getDaysArray = (startDate: Date, numDays: number = 7) => {
    const days = [];
    const currentDate = new Date(startDate);
    for (let i = 0; i < numDays; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };
  const daysToShow = getDaysArray(viewStartDate);
  // Format date as YYYY-MM-DD for the availability object
  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };
  // Format date for display
  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  // Check if a date is tomorrow
  const isTomorrow = (date: Date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.getDate() === tomorrow.getDate() && date.getMonth() === tomorrow.getMonth() && date.getFullYear() === tomorrow.getFullYear();
  };
  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newStartDate = new Date(viewStartDate);
    newStartDate.setDate(newStartDate.getDate() - 7);
    setViewStartDate(newStartDate);
  };
  // Navigate to next week
  const goToNextWeek = () => {
    const newStartDate = new Date(viewStartDate);
    newStartDate.setDate(newStartDate.getDate() + 7);
    setViewStartDate(newStartDate);
  };
  // Reset to current week
  const goToCurrentWeek = () => {
    setViewStartDate(new Date());
  };
  // Get unique truck types for filtering
  const truckTypes = Array.from(new Set(availableTrucks.map(truck => truck.type)));
  // Filter trucks based on selected criteria
  const filteredTrucks = availableTrucks.filter(truck => {
    if (selectedCompany !== 'all' && truck.truckingCompanyId !== selectedCompany) {
      return false;
    }
    if (selectedTruckType !== 'all' && truck.type !== selectedTruckType) {
      return false;
    }
    if (showOnlyAvailable) {
      const tomorrowKey = getDateString(1);
      return truck.status === 'available' && truck.availability[tomorrowKey] === true;
    }
    return true;
  });
  // Get company info for a truck
  const getCompanyInfo = (truckingCompanyId: string) => {
    return truckingCompanies.find(company => company.id === truckingCompanyId);
  };
  // Calculate availability stats
  const totalTrucks = filteredTrucks.length;
  const availableToday = filteredTrucks.filter(truck => truck.status === 'available' && truck.availability[getDateString(0)] === true).length;
  const availableTomorrow = filteredTrucks.filter(truck => truck.status === 'available' && truck.availability[getDateString(1)] === true).length;
  return <div className="bg-white rounded-lg shadow p-6" data-id="element-537">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4" data-id="element-538">
        <div data-id="element-539">
          <h2 className="text-xl font-bold mb-2" data-id="element-540">
            Available Trucks from Partner Companies
          </h2>
          <p className="text-sm text-gray-600" data-id="element-541">
            View and manage trucks that have been made available to you by
            trucking companies
          </p>
          <div className="flex items-center gap-4 mt-2 text-sm" data-id="element-542">
            <div className="flex items-center gap-1" data-id="element-543">
              <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-544"></div>
              <span data-id="element-545">Available Today: {availableToday}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-546">
              <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-547"></div>
              <span data-id="element-548">Available Tomorrow: {availableTomorrow}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-549">
              <div className="w-3 h-3 bg-gray-400 rounded-full" data-id="element-550"></div>
              <span data-id="element-551">Total: {totalTrucks}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-2" data-id="element-552">
          <button onClick={() => window.location.reload()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-553">
            <RefreshCwIcon size={16} data-id="element-554" />
            Refresh
          </button>
          <div className="flex items-center border rounded-md" data-id="element-555">
            <button onClick={goToPreviousWeek} className="p-2 hover:bg-gray-100 border-r" data-id="element-556">
              <ChevronLeftIcon size={16} data-id="element-557" />
            </button>
            <button onClick={goToCurrentWeek} className="px-3 py-2 text-sm hover:bg-gray-100 border-r" data-id="element-558">
              This Week
            </button>
            <button onClick={goToNextWeek} className="p-2 hover:bg-gray-100" data-id="element-559">
              <ChevronRightIcon size={16} data-id="element-560" />
            </button>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg" data-id="element-561">
        <div className="flex items-center gap-2" data-id="element-562">
          <FilterIcon size={16} className="text-gray-500" data-id="element-563" />
          <span className="text-sm font-medium" data-id="element-564">Filters:</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-565">
          <label className="text-sm" data-id="element-566">Company:</label>
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="border border-gray-300 rounded-md p-1 text-sm" data-id="element-567">
            <option value="all" data-id="element-568">All Companies</option>
            {truckingCompanies.map(company => <option key={company.id} value={company.id} data-id="element-569">
                {company.name}
              </option>)}
          </select>
        </div>
        <div className="flex items-center gap-2" data-id="element-570">
          <label className="text-sm" data-id="element-571">Truck Type:</label>
          <select value={selectedTruckType} onChange={e => setSelectedTruckType(e.target.value)} className="border border-gray-300 rounded-md p-1 text-sm" data-id="element-572">
            <option value="all" data-id="element-573">All Types</option>
            {truckTypes.map(type => <option key={type} value={type} data-id="element-574">
                {type}
              </option>)}
          </select>
        </div>
        <div className="flex items-center gap-2" data-id="element-575">
          <label className="flex items-center gap-1 text-sm" data-id="element-576">
            <input type="checkbox" checked={showOnlyAvailable} onChange={e => setShowOnlyAvailable(e.target.checked)} className="rounded" data-id="element-577" />
            Available Tomorrow Only
          </label>
        </div>
      </div>
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6" data-id="element-578">
        <div className="flex items-start gap-2" data-id="element-579">
          <InfoIcon size={18} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-580" />
          <div className="text-sm text-blue-800" data-id="element-581">
            <p className="font-medium mb-1" data-id="element-582">How to use available trucks:</p>
            <p data-id="element-583">
              • Green checkmarks indicate truck availability on specific dates
            </p>
            <p data-id="element-584">
              • Click "Request Truck" to reserve a truck for a specific date
            </p>
            <p data-id="element-585">• Contact drivers directly to coordinate job details</p>
            <p data-id="element-586">
              • Rates shown are hourly rates negotiated with each trucking
              company
            </p>
          </div>
        </div>
      </div>
      {/* Trucks Table */}
      {filteredTrucks.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg" data-id="element-587">
          <TruckIcon size={40} className="mx-auto text-gray-400 mb-2" data-id="element-588" />
          <p className="text-gray-600" data-id="element-589">No trucks match your current filters</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-590">
            Try adjusting your filters or contact trucking companies to request
            truck availability
          </p>
        </div> : <div className="overflow-x-auto" data-id="element-591">
          <table className="w-full border-collapse" data-id="element-592">
            <thead data-id="element-593">
              <tr className="bg-gray-50" data-id="element-594">
                <th className="text-left p-3 border-b sticky left-0 bg-gray-50 z-10 min-w-[200px]" data-id="element-595">
                  Truck Details
                </th>
                <th className="text-left p-3 border-b min-w-[160px]" data-id="element-596">
                  Driver Info
                </th>
                <th className="text-left p-3 border-b min-w-[120px]" data-id="element-597">Rate</th>
                <th className="text-left p-3 border-b min-w-[140px]" data-id="element-598">
                  Location
                </th>
                {daysToShow.map((date, index) => <th key={index} className={`text-center p-3 border-b min-w-[80px] ${isToday(date) ? 'bg-green-50 text-green-800' : isTomorrow(date) ? 'bg-blue-50 text-blue-800' : 'text-gray-600'}`} data-id="element-599">
                    <div className="text-xs" data-id="element-600">
                      {date.toLocaleDateString('en-US', {
                  weekday: 'short'
                })}
                    </div>
                    <div className="text-xs" data-id="element-601">
                      {date.toLocaleDateString('en-US', {
                  month: 'numeric',
                  day: 'numeric'
                })}
                    </div>
                    {isToday(date) && <div className="text-xs bg-green-100 text-green-800 rounded-full px-1 mt-1" data-id="element-602">
                        Today
                      </div>}
                    {isTomorrow(date) && <div className="text-xs bg-blue-100 text-blue-800 rounded-full px-1 mt-1" data-id="element-603">
                        Tomorrow
                      </div>}
                  </th>)}
                <th className="text-left p-3 border-b min-w-[120px]" data-id="element-604">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-605">
              {filteredTrucks.map(truck => {
            const company = getCompanyInfo(truck.truckingCompanyId);
            return <tr key={truck.id} className="hover:bg-gray-50" data-id="element-606">
                    <td className="p-3 border-b sticky left-0 bg-white hover:bg-gray-50 z-10" data-id="element-607">
                      <div className="flex items-start gap-3" data-id="element-608">
                        <TruckIcon size={20} className={truck.status === 'available' ? 'text-green-500' : truck.status === 'maintenance' ? 'text-orange-500' : 'text-blue-500'} data-id="element-609" />
                        <div data-id="element-610">
                          <div className="font-medium" data-id="element-611">{truck.truckNumber}</div>
                          <div className="text-xs text-gray-500" data-id="element-612">
                            {truck.make} {truck.model} ({truck.year})
                          </div>
                          <div className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full mt-1 inline-block" data-id="element-613">
                            {truck.type}
                          </div>
                          <div className="text-xs text-gray-600 mt-1" data-id="element-614">
                            {company?.name}
                          </div>
                          <div className="flex items-center gap-1 mt-1" data-id="element-615">
                            <div className={`w-2 h-2 rounded-full ${truck.status === 'available' ? 'bg-green-500' : truck.status === 'maintenance' ? 'bg-orange-500' : 'bg-blue-500'}`} data-id="element-616"></div>
                            <span className="text-xs text-gray-500 capitalize" data-id="element-617">
                              {truck.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 border-b" data-id="element-618">
                      {truck.driverName ? <div data-id="element-619">
                          <div className="flex items-center gap-1 text-sm" data-id="element-620">
                            <UserIcon size={14} className="text-gray-400" data-id="element-621" />
                            <span className="font-medium" data-id="element-622">
                              {truck.driverName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1" data-id="element-623">
                            <PhoneIcon size={12} className="text-gray-400" data-id="element-624" />
                            <span data-id="element-625">{truck.driverPhone}</span>
                          </div>
                        </div> : <div className="text-xs text-gray-400" data-id="element-626">
                          No driver assigned
                        </div>}
                    </td>
                    <td className="p-3 border-b" data-id="element-627">
                      <div className="text-sm font-medium text-green-600" data-id="element-628">
                        ${truck.hourlyRate.toFixed(2)}/hr
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-629">Hourly Rate</div>
                    </td>
                    <td className="p-3 border-b" data-id="element-630">
                      <div className="flex items-center gap-1 text-sm" data-id="element-631">
                        <MapPinIcon size={14} className="text-gray-400" data-id="element-632" />
                        <span data-id="element-633">
                          {truck.currentLocation || 'Location not available'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-634">
                        Updated:{' '}
                        {new Date(truck.lastUpdated).toLocaleDateString()}
                      </div>
                    </td>
                    {daysToShow.map((date, index) => {
                const dateKey = formatDateKey(date);
                const isAvailable = truck.availability[dateKey] === true && truck.status === 'available';
                return <td key={index} className={`p-3 border-b text-center ${isToday(date) ? 'bg-green-50' : isTomorrow(date) ? 'bg-blue-50' : ''}`} data-id="element-635">
                          {truck.status !== 'available' ? <div className="flex items-center justify-center" data-id="element-636">
                              <AlertTriangleIcon size={16} className="text-orange-500" data-id="element-637" />
                            </div> : isAvailable ? <button onClick={() => onRequestTruck(truck.id, dateKey)} className="w-8 h-8 bg-green-100 hover:bg-green-200 text-green-700 rounded-full flex items-center justify-center mx-auto" title={`Request ${truck.truckNumber} for ${formatDateDisplay(date)}`} data-id="element-638">
                              <CheckCircleIcon size={16} data-id="element-639" />
                            </button> : <div className="w-8 h-8 bg-red-100 text-red-700 rounded-full flex items-center justify-center mx-auto" data-id="element-640">
                              <XCircleIcon size={16} data-id="element-641" />
                            </div>}
                        </td>;
              })}
                    <td className="p-3 border-b" data-id="element-642">
                      <div className="flex flex-col gap-1" data-id="element-643">
                        {truck.driverPhone && <button onClick={() => onContactDriver(truck.id)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 justify-center px-2 py-1 border border-blue-200 rounded hover:bg-blue-50" data-id="element-644">
                            <MessageSquareIcon size={12} data-id="element-645" />
                            Contact Driver
                          </button>}
                        {company && <a href={`tel:${company.phone}`} className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 justify-center px-2 py-1 border border-purple-200 rounded hover:bg-purple-50" data-id="element-646">
                            <PhoneIcon size={12} data-id="element-647" />
                            Call Company
                          </a>}
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      {/* Summary Section */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg" data-id="element-648">
        <h3 className="font-medium mb-2" data-id="element-649">Partner Company Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-650">
          {truckingCompanies.map(company => {
          const companyTrucks = availableTrucks.filter(truck => truck.truckingCompanyId === company.id);
          const availableCount = companyTrucks.filter(truck => truck.status === 'available' && truck.availability[getDateString(1)] === true).length;
          return <div key={company.id} className="bg-white p-3 rounded border" data-id="element-651">
                <div className="flex items-center justify-between mb-2" data-id="element-652">
                  <h4 className="font-medium text-sm" data-id="element-653">{company.name}</h4>
                  <div className="flex items-center gap-1" data-id="element-654">
                    {Array.from({
                  length: Math.floor(company.rating)
                }).map((_, i) => <div key={i} className="w-3 h-3 bg-yellow-400 rounded-full" data-id="element-655"></div>)}
                    <span className="text-xs text-gray-500 ml-1" data-id="element-656">
                      {company.rating}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-600" data-id="element-657">
                  <div data-id="element-658">Total assigned to you: {companyTrucks.length}</div>
                  <div data-id="element-659">Available tomorrow: {availableCount}</div>
                  <div className="flex items-center gap-1 mt-1" data-id="element-660">
                    <PhoneIcon size={10} data-id="element-661" />
                    {company.phone}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
};